import React, { useState, useMemo, useRef, useEffect } from 'react';
import useAppChoices from '../../hooks/useAppChoices';
import { TASK_ROLES } from '../../helpers/constants';
import Select, { components } from 'react-select';
import './style.scss';

export default function AssignedProcessorsMultiSelect({ id, error, onChange, selectedProcessorsID, isEditing }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const selectRef = useRef(null);

  const user = useAppChoices('users');
  const processors = user.filter(u => TASK_ROLES.includes(u.role));
  const AssignedProcessors = isEditing ? processors : processors.filter(p => p.isEnabled);
  const assignedProcessorsFormatted = AssignedProcessors.map(assignedProcessor => ({
    ...assignedProcessor,
    value: assignedProcessor._id,
    label: assignedProcessor.name
  })).sort((a, b) => a.label.localeCompare(b.label));

  const [assignedProcessors, setAssignedProcessors] = useState([]);

  const enabledProcessors = useMemo(
    () => assignedProcessorsFormatted.filter(p => p.label.toLowerCase().includes(searchTerm.toLowerCase())),
    [assignedProcessorsFormatted, searchTerm]
  );

  const disabledProcessors = useMemo(
    () => {
      const selectedIds = new Set((assignedProcessors || []).map(p => p._id));
      return assignedProcessorsFormatted.filter(p => !p.isEnabled && p.label.toLowerCase().includes(searchTerm.toLowerCase()) && selectedIds.has(p._id))
    },
    [assignedProcessorsFormatted, searchTerm, assignedProcessors]
  );

  const groupedOptions = [
    {
      label: 'Active Processors',
      options: enabledProcessors
    },
    ...(disabledProcessors.length > 0 ? [{
      label: 'Inactive Processors',
      options: disabledProcessors
    }] : [])
  ];

  useEffect(() => {
    const selectedProcessors = assignedProcessorsFormatted.filter(assignedProcessor =>
      selectedProcessorsID.includes(assignedProcessor._id)
    );
    setAssignedProcessors(selectedProcessors);
  }, [selectedProcessorsID]);

  const handleOnChange = e => {
    setAssignedProcessors(e);
    onChange(e);
  };

  // Custom search input inside the options menu
  const CustomMenuList = props => {
    return (
      <div>
        <div style={{ padding: '10px' }}>
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={e => {
              e.stopPropagation();
              setSearchTerm(e.target.value);
            }}
            onKeyDown={e => {
              e.stopPropagation();
            }}
            className="form-control form-control-sm search-input"
            autoFocus
          />
        </div>
        <components.MenuList {...props}>{props.children}</components.MenuList>
      </div>
    );
  };

  const CustomOption = props => {
    return (
      <div>
        <components.Option {...props} className="select-option-items">
          <input type="checkbox" checked={props.isSelected} onChange={() => { }} /> <label>{props.label} </label>
        </components.Option>
      </div>
    );
  };

  // Close menu when clicking outside
  // Enable multiple selection of options
  useEffect(() => {
    const handleClickOutside = event => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="assigned-processors" ref={selectRef}>
      <input
        type="text"
        readOnly
        placeholder="Select"
        className={`form-control form-control-sm select-input ${error && 'input-invalid'}`}
        onClick={() => setIsMenuOpen(true)}
        value={
          assignedProcessors?.length > 0
            ? assignedProcessors?.length > 1
              ? `${assignedProcessors[0].name} and ${assignedProcessors.length - 1} others`
              : assignedProcessors[0].name
            : ''
        }
      />
      <Select
        id={id}
        required
        isMulti
        components={{ Option: CustomOption, MenuList: CustomMenuList }}
        isSearchable={false}
        hideSelectedOptions={false}
        menuIsOpen={isMenuOpen}
        onMenuOpen={() => setIsMenuOpen(true)}
        onMenuClose={() => setIsMenuOpen(false)}
        className={`select-assigned-processors ${error && 'input-invalid'}`}
        classNamePrefix="select"
        onChange={handleOnChange}
        placeholder="Select"
        options={groupedOptions}
        value={assignedProcessors}
        closeMenuOnSelect={false}
        menuPlacement={'auto'}
      />
    </div>
  );
}
